/**
 * RESERVES PAGE - Apple Liquid Glass Design System
 * macOS Tahoe 26.2 & iOS 26.2
 *
 * Track 3rd bi-weekly payments that go to reserves
 * Distribute reserves based on partner share percentages
 */

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  Lock,
  Plus,
  TrendingUp,
  Calendar,
  DollarSign,
  Users,
  ArrowRight,
  Check,
  RefreshCw,
  Sparkles,
  PiggyBank,
  ChevronRight
} from 'lucide-react'
import { Button } from '../../components/ui/Button'
import { Modal } from '../../components/ui/Modal'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { GlassCard, ContentCard } from '../../components/ui/GlassCard'
import { Badge, StatusBadge } from '../../components/ui/Badge'
import { StatCard, StatCardGrid } from '../../components/ui/StatCard'
import { SkeletonStatCard, SkeletonTable } from '../../components/ui/Skeleton'
import { apiClient } from '../../lib/api'

// Animation configuration
const springConfig = {
  type: "spring" as const,
  damping: 25,
  stiffness: 300
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.1
    }
  }
}

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: springConfig
  }
}

const listItemVariants = {
  hidden: { opacity: 0, x: -20 },
  visible: {
    opacity: 1,
    x: 0,
    transition: springConfig
  }
}

interface Reserve {
  id: number
  date: string
  client_id: number | null
  client_name: string | null
  client_company: string | null
  amount: number
  currency: 'USD' | 'PKR'
  description: string | null
  year: number
  month: string
  is_distributed: number
  distributed_at: string | null
}

interface Client {
  id: number
  name: string
  company: string
}

interface ReserveTotals {
  currency: string
  undistributed: number
  distributed: number
  total: number
}

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
]

export function ReservesPage() {
  const [loading, setLoading] = useState(true)
  const [reserves, setReserves] = useState<Reserve[]>([])
  const [totals, setTotals] = useState<ReserveTotals[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear())
  const [error, setError] = useState<string | null>(null)

  // Modal states
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [isDistributeModalOpen, setIsDistributeModalOpen] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [distributeSuccess, setDistributeSuccess] = useState(false)

  // Form state
  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    client_id: '',
    amount: '',
    currency: 'USD' as 'USD' | 'PKR',
    description: '',
    year: new Date().getFullYear(),
    month: MONTHS[new Date().getMonth()]
  })

  // Distribute form
  const [distributeMonth, setDistributeMonth] = useState(MONTHS[new Date().getMonth()])
  const [distributeYear, setDistributeYear] = useState(new Date().getFullYear())

  useEffect(() => {
    fetchData()
  }, [selectedYear])

  const fetchData = async () => {
    try {
      setLoading(true)
      setError(null)

      const [reservesRes, clientsRes] = await Promise.all([
        apiClient.get(`/reserves?year=${selectedYear}`),
        apiClient.get('/clients')
      ])

      if (reservesRes.success) {
        setReserves(reservesRes.data || [])
        setTotals(reservesRes.totals || [])
      }

      if (clientsRes.success) {
        setClients(clientsRes.data || [])
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load reserves')
    } finally {
      setLoading(false)
    }
  }

  const handleAddReserve = async () => {
    try {
      setIsSubmitting(true)
      setError(null)

      const response = await apiClient.post('/reserves', {
        ...formData,
        client_id: formData.client_id ? parseInt(formData.client_id) : null,
        amount: parseFloat(formData.amount)
      })

      if (response.success) {
        setIsAddModalOpen(false)
        setFormData({
          date: new Date().toISOString().split('T')[0],
          client_id: '',
          amount: '',
          currency: 'USD',
          description: '',
          year: new Date().getFullYear(),
          month: MONTHS[new Date().getMonth()]
        })
        await fetchData()
      }
    } catch (err: any) {
      setError(err.message || 'Failed to add reserve')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDistribute = async () => {
    try {
      setIsSubmitting(true)
      setError(null)

      const response = await apiClient.post('/reserves/distribute', {
        year: distributeYear,
        month: distributeMonth
      })

      if (response.success) {
        setDistributeSuccess(true)
        await fetchData()
      }
    } catch (err: any) {
      setError(err.message || 'Failed to distribute reserves')
    } finally {
      setIsSubmitting(false)
    }
  }

  const formatCurrency = (amount: number, currency: string) => {
    if (currency === 'PKR') {
      return new Intl.NumberFormat('en-PK', {
        style: 'currency',
        currency: 'PKR',
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(amount)
    }
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount)
  }

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    })
  }

  const undistributedUSD = totals.find(t => t.currency === 'USD')?.undistributed || 0
  const undistributedPKR = totals.find(t => t.currency === 'PKR')?.undistributed || 0
  const totalUSD = totals.find(t => t.currency === 'USD')?.total || 0
  const totalPKR = totals.find(t => t.currency === 'PKR')?.total || 0

  // Loading state
  if (loading) {
    return (
      <div className="p-6 max-w-[1600px] mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <div className="h-8 w-44 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg animate-pulse" />
            <div className="h-4 w-64 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-lg animate-pulse" />
          </div>
          <div className="flex gap-3">
            <div className="h-10 w-24 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-xl animate-pulse" />
            <div className="h-10 w-32 bg-light-bg-tertiary dark:bg-dark-bg-tertiary rounded-xl animate-pulse" />
          </div>
        </div>
        <StatCardGrid columns={4}>
          <SkeletonStatCard />
          <SkeletonStatCard />
          <SkeletonStatCard />
          <SkeletonStatCard />
        </StatCardGrid>
        <SkeletonTable rows={6} />
      </div>
    )
  }

  return (
    <motion.div
      className="p-6 max-w-[1600px] mx-auto"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      {/* Header */}
      <motion.div variants={itemVariants} className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-title2 font-bold text-light-text-primary dark:text-dark-text-primary tracking-tight">
            Reserve Fund
          </h1>
          <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary mt-1">
            Track 3rd bi-weekly payments and distribute to partners
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select
            value={selectedYear.toString()}
            onChange={(value) => setSelectedYear(parseInt(value))}
            options={[
              { value: '2024', label: '2024' },
              { value: '2025', label: '2025' },
              { value: '2026', label: '2026' }
            ]}
          />
          <Button
            variant="secondary"
            icon={<RefreshCw className="w-4 h-4" />}
            onClick={fetchData}
          >
            Refresh
          </Button>
          <Button
            variant="primary"
            icon={<Plus className="w-4 h-4" />}
            onClick={() => setIsAddModalOpen(true)}
          >
            Add Reserve
          </Button>
        </div>
      </motion.div>

      {/* Error Alert */}
      <AnimatePresence>
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: 'auto' }}
            exit={{ opacity: 0, y: -10, height: 0 }}
            className="mb-6"
          >
            <GlassCard className="border border-red/20 bg-red/5">
              <div className="flex items-center gap-3 p-4">
                <div className="w-10 h-10 rounded-xl bg-red/10 flex items-center justify-center">
                  <Lock className="w-5 h-5 text-red" />
                </div>
                <div className="flex-1">
                  <p className="text-red font-medium">{error}</p>
                </div>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setError(null)}
                >
                  Dismiss
                </Button>
              </div>
            </GlassCard>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Summary Stats */}
      <motion.div variants={itemVariants}>
        <StatCardGrid columns={4}>
          <StatCard
            title="Undistributed USD"
            value={formatCurrency(undistributedUSD, 'USD')}
            icon={DollarSign}
            color="green"
            badge={<Badge variant="green">Ready</Badge>}
          />
          <StatCard
            title="Undistributed PKR"
            value={formatCurrency(undistributedPKR, 'PKR')}
            icon={PiggyBank}
            color="blue"
            badge={<Badge variant="blue">Ready</Badge>}
          />
          <StatCard
            title={`Total USD (${selectedYear})`}
            value={formatCurrency(totalUSD, 'USD')}
            icon={Lock}
            color="purple"
            badge={<Badge variant="purple">Year</Badge>}
          />
          <StatCard
            title={`Total PKR (${selectedYear})`}
            value={formatCurrency(totalPKR, 'PKR')}
            icon={TrendingUp}
            color="orange"
            badge={<Badge variant="orange">Year</Badge>}
          />
        </StatCardGrid>
      </motion.div>

      {/* Distribute Button */}
      {(undistributedUSD > 0 || undistributedPKR > 0) && (
        <motion.div variants={itemVariants} className="mt-6">
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.99 }}
            onClick={() => {
              setDistributeSuccess(false)
              setIsDistributeModalOpen(true)
            }}
            className="w-full relative overflow-hidden bg-gradient-to-r from-green to-emerald-500 rounded-2xl p-5 shadow-lg shadow-green/20 transition-all duration-300 group"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 translate-x-[-200%] group-hover:translate-x-[200%] transition-transform duration-700" />
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-2xl flex items-center justify-center">
                  <Users className="w-6 h-6 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-headline font-bold text-white mb-0.5">
                    Distribute Reserves to Partners
                  </h3>
                  <p className="text-white/80 text-footnote">
                    Split by share percentage • {formatCurrency(undistributedUSD, 'USD')} + {formatCurrency(undistributedPKR, 'PKR')} available
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-white">
                <span className="font-semibold text-body">Distribute Now</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </div>
          </motion.button>
        </motion.div>
      )}

      {/* Reserves List */}
      <motion.div variants={itemVariants} className="mt-6">
        <ContentCard>
          <div className="p-5 border-b border-black/[0.04] dark:border-white/[0.04]">
            <h2 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary">
              Reserve Entries ({selectedYear})
            </h2>
          </div>

          {reserves.length === 0 ? (
            <div className="text-center py-16">
              <motion.div
                initial={{ scale: 0.8, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={springConfig}
                className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-purple/10 to-pink/10 flex items-center justify-center"
              >
                <Lock className="w-10 h-10 text-light-text-quaternary dark:text-dark-text-quaternary" />
              </motion.div>
              <h3 className="text-headline font-semibold text-light-text-primary dark:text-dark-text-primary mb-2">
                No Reserves Yet
              </h3>
              <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary mb-6">
                Add 3rd bi-weekly payments when months have extra payment periods
              </p>
              <Button
                variant="primary"
                icon={<Plus className="w-4 h-4" />}
                onClick={() => setIsAddModalOpen(true)}
              >
                Add First Reserve
              </Button>
            </div>
          ) : (
            <motion.div
              className="divide-y divide-black/[0.04] dark:divide-white/[0.04]"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {reserves.map((reserve, index) => (
                <motion.div
                  key={reserve.id}
                  variants={listItemVariants}
                  custom={index}
                  whileHover={{ backgroundColor: 'rgba(0, 0, 0, 0.02)' }}
                  className="flex items-center gap-4 p-4 transition-colors group"
                >
                  {/* Status Icon */}
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    className={`w-12 h-12 rounded-2xl flex items-center justify-center ${
                      reserve.is_distributed
                        ? 'bg-gradient-to-br from-green/10 to-green/5'
                        : reserve.currency === 'USD'
                        ? 'bg-gradient-to-br from-blue/10 to-blue/5'
                        : 'bg-gradient-to-br from-purple/10 to-purple/5'
                    }`}
                  >
                    {reserve.is_distributed ? (
                      <Check className="w-6 h-6 text-green" />
                    ) : (
                      <DollarSign className={`w-6 h-6 ${
                        reserve.currency === 'USD' ? 'text-blue' : 'text-purple'
                      }`} />
                    )}
                  </motion.div>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4">
                      <div className="min-w-0">
                        <h4 className="font-semibold text-body text-light-text-primary dark:text-dark-text-primary truncate">
                          {reserve.client_company || reserve.client_name || 'General Reserve'}
                        </h4>
                        <div className="flex items-center gap-2 mt-1">
                          <span className="flex items-center gap-1.5 text-footnote text-light-text-tertiary dark:text-dark-text-tertiary">
                            <Calendar className="w-3.5 h-3.5" />
                            {formatDate(reserve.date)}
                          </span>
                          {reserve.description && (
                            <>
                              <span className="text-light-text-quaternary">•</span>
                              <span className="text-footnote text-light-text-tertiary dark:text-dark-text-tertiary truncate">
                                {reserve.description}
                              </span>
                            </>
                          )}
                        </div>
                      </div>

                      <div className="text-right flex-shrink-0">
                        <div className={`text-title3 font-bold ${
                          reserve.currency === 'USD' ? 'text-blue' : 'text-purple'
                        }`}>
                          {formatCurrency(reserve.amount, reserve.currency)}
                        </div>
                        <div className="mt-1">
                          <StatusBadge
                            status={reserve.is_distributed ? 'active' : 'pending'}
                          >
                            {reserve.is_distributed ? 'Distributed' : 'Pending'}
                          </StatusBadge>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Chevron */}
                  <ChevronRight className="w-5 h-5 text-light-text-quaternary dark:text-dark-text-quaternary opacity-0 group-hover:opacity-100 transition-opacity" />
                </motion.div>
              ))}
            </motion.div>
          )}
        </ContentCard>
      </motion.div>

      {/* Add Reserve Modal */}
      <Modal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        title="Add Reserve (3rd Payment)"
        size="md"
      >
        <div className="space-y-5">
          <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary">
            Record a 3rd bi-weekly payment that goes to the reserve fund.
          </p>

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Date"
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
            />
            <Select
              label="Currency"
              value={formData.currency}
              onChange={(value) => setFormData({ ...formData, currency: value as 'USD' | 'PKR' })}
              options={[
                { value: 'USD', label: 'USD ($)' },
                { value: 'PKR', label: 'PKR (Rs.)' }
              ]}
            />
          </div>

          <Input
            label="Amount"
            type="number"
            placeholder="0.00"
            value={formData.amount}
            onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
          />

          <Select
            label="Client (Optional)"
            value={formData.client_id}
            onChange={(value) => setFormData({ ...formData, client_id: value })}
            options={[
              { value: '', label: 'Select client...' },
              ...clients.map(c => ({ value: c.id.toString(), label: `${c.name} - ${c.company}` }))
            ]}
          />

          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Month"
              value={formData.month}
              onChange={(value) => setFormData({ ...formData, month: value })}
              options={MONTHS.map(m => ({ value: m, label: m }))}
            />
            <Select
              label="Year"
              value={formData.year.toString()}
              onChange={(value) => setFormData({ ...formData, year: parseInt(value) })}
              options={[
                { value: '2024', label: '2024' },
                { value: '2025', label: '2025' },
                { value: '2026', label: '2026' }
              ]}
            />
          </div>

          <Input
            label="Description (Optional)"
            placeholder="e.g., 3rd payment - extra week"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          />

          <div className="flex gap-3 pt-2">
            <Button
              variant="secondary"
              fullWidth
              onClick={() => setIsAddModalOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              fullWidth
              icon={<Plus className="w-4 h-4" />}
              onClick={handleAddReserve}
              disabled={isSubmitting || !formData.amount}
              isLoading={isSubmitting}
            >
              Add Reserve
            </Button>
          </div>
        </div>
      </Modal>

      {/* Distribute Modal */}
      <Modal
        isOpen={isDistributeModalOpen}
        onClose={() => {
          setIsDistributeModalOpen(false)
          setDistributeSuccess(false)
        }}
        title={distributeSuccess ? 'Distribution Complete!' : 'Distribute Reserves'}
        size="md"
      >
        {distributeSuccess ? (
          <div className="text-center py-6">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", damping: 15, stiffness: 300 }}
              className="w-20 h-20 bg-gradient-to-br from-green/20 to-green/10 rounded-2xl flex items-center justify-center mx-auto mb-4"
            >
              <Sparkles className="w-10 h-10 text-green" />
            </motion.div>
            <h3 className="text-title2 font-bold text-light-text-primary dark:text-dark-text-primary mb-2">
              Reserves Distributed!
            </h3>
            <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary mb-6">
              All partners have received their share based on their percentage.
            </p>
            <Button
              variant="primary"
              onClick={() => {
                setIsDistributeModalOpen(false)
                setDistributeSuccess(false)
              }}
            >
              Done
            </Button>
          </div>
        ) : (
          <div className="space-y-5">
            <p className="text-subheadline text-light-text-secondary dark:text-dark-text-secondary">
              Distribute reserves to all active partners based on their share percentages.
            </p>

            <div className="p-4 bg-gradient-to-br from-purple/5 to-pink/5 rounded-2xl">
              <div className="flex items-center justify-between mb-3">
                <span className="text-footnote text-light-text-secondary dark:text-dark-text-secondary">Available USD</span>
                <span className="font-bold text-body text-blue">{formatCurrency(undistributedUSD, 'USD')}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-footnote text-light-text-secondary dark:text-dark-text-secondary">Available PKR</span>
                <span className="font-bold text-body text-purple">{formatCurrency(undistributedPKR, 'PKR')}</span>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <Select
                label="Month"
                value={distributeMonth}
                onChange={setDistributeMonth}
                options={MONTHS.map(m => ({ value: m, label: m }))}
              />
              <Select
                label="Year"
                value={distributeYear.toString()}
                onChange={(value) => setDistributeYear(parseInt(value))}
                options={[
                  { value: '2024', label: '2024' },
                  { value: '2025', label: '2025' },
                  { value: '2026', label: '2026' }
                ]}
              />
            </div>

            <div className="flex gap-3 pt-2">
              <Button
                variant="secondary"
                fullWidth
                onClick={() => setIsDistributeModalOpen(false)}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                fullWidth
                icon={<Users className="w-4 h-4" />}
                onClick={handleDistribute}
                disabled={isSubmitting}
                isLoading={isSubmitting}
              >
                Distribute by Share %
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </motion.div>
  )
}
